import React, { useState, useCallback } from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useAppDispatch } from "@app-redux/store";
import {
  updateOpportunity,
  deleteOpportunity,
} from "@app-redux/thunk/opportunityThunk";
import type { Opportunity } from "@app-redux/slices/opportunitySlice";
import type { ColumnDef, OpportunityField } from "./columnConfig";
import styles from "./styles.module.css";

type Props = {
  opportunity: Opportunity;
  isAdmin: boolean;
  columns: ColumnDef[];
};

type EditValues = Record<OpportunityField, string | null>;

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("MMM D, YYYY") : "—";
};

const buildEditValues = (opp: Opportunity): EditValues => ({
  first_us_date: opp.first_us_date,
  artist_tour: opp.artist_tour,
  ld: opp.ld,
  previous_tour_ld: opp.previous_tour_ld,
  christie_rep_ld: opp.christie_rep_ld,
  production_manager: opp.production_manager,
  previous_tour_pm: opp.previous_tour_pm,
  christie_rep_pm: opp.christie_rep_pm,
  band_management: opp.band_management,
  location: opp.location,
  agency: opp.agency,
  producer: opp.producer,
});

export const OpportunityRow = ({ opportunity, isAdmin, columns }: Props) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>(
    buildEditValues(opportunity)
  );

  const handleEdit = useCallback(() => {
    setEditValues(buildEditValues(opportunity));
    setIsEditing(true);
  }, [opportunity]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(() => {
    dispatch(
      updateOpportunity({
        id: opportunity.id,
        payload: editValues as Parameters<typeof updateOpportunity>[0]["payload"],
      })
    );
    setIsEditing(false);
  }, [dispatch, opportunity.id, editValues]);

  const handleDelete = useCallback(() => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      dispatch(deleteOpportunity(opportunity.id));
    }
  }, [dispatch, opportunity.id]);

  const handleChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setEditValues((prev) => ({ ...prev, [field]: e.target.value }));
    },
    []
  );

  const handleDateChange = useCallback(
    (field: string) => (newVal: Dayjs | null) => {
      setEditValues((prev) => ({
        ...prev,
        [field]: newVal ? newVal.toISOString() : null,
      }));
    },
    []
  );

  if (isEditing && isAdmin) {
    return (
      <tr>
        {columns.map((col) => (
          <td key={col.field}>
            {col.isDate ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={
                    editValues[col.field]
                      ? dayjs(editValues[col.field])
                      : null
                  }
                  onChange={handleDateChange(col.field)}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "standard",
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={editValues[col.field] ?? ""}
                onChange={handleChange(col.field)}
              />
            )}
          </td>
        ))}
        <td className={styles.actionsCell} style={{ overflow: 'visible', textOverflow: 'clip' }}>
          <IconButton size="small" onClick={handleSave} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleCancel}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      {columns.map((col) => (
        <td key={col.field}>
          {col.isDate
            ? formatDate(opportunity[col.field])
            : opportunity[col.field] ?? ""}
        </td>
      ))}
      {isAdmin && (
        <td className={styles.actionsCell} style={{ overflow: 'visible', textOverflow: 'clip' }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={handleDelete} color="error">
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </td>
      )}
    </tr>
  );
};
